import { useEffect, useState, useRef } from "react";
import JSZip from "jszip";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Upload, FileSpreadsheet, AlertTriangle, ArrowLeft, CheckCircle2, Sparkles, Undo2, Download, ImageIcon, RotateCcw, Loader2 } from "lucide-react";
import { api, formatCOP, formatNumber } from "@/lib/api";
import { toast } from "sonner";
import AgruparFamiliasStep from "./AgruparFamiliasStep";

/**
 * Feb/2026: Genera una versión "ligera" del XLSX quitando las imágenes embebidas.
 * Muchos Excels de proveedor pesan >100MB pero 90-95% son las fotos incrustadas.
 * Para el PREVIEW no necesitamos las fotos (las extraemos aparte). Esta versión
 * pesa ~5-10 MB (solo XML) y sube en segundos.
 *
 * @param {File} file — archivo original
 * @returns {Promise<{lightBlob: Blob, originalSize: number, lightSize: number, imagenesRemovidas: number}>}
 */
async function generarXlsxLigero(file) {
  const originalBuf = await file.arrayBuffer();
  const zip = await JSZip.loadAsync(originalBuf);
  let imagenesRemovidas = 0;
  const paths = Object.keys(zip.files);
  for (const p of paths) {
    // Remover cualquier archivo de imagen dentro de xl/media/ o rutas típicas.
    // Las fotos se extraen aparte en el flujo de "extraer imágenes por SKU".
    if (p.startsWith("xl/media/") || /\.(png|jpe?g|gif|bmp|webp|emf|wmf)$/i.test(p)) {
      zip.remove(p);
      imagenesRemovidas += 1;
    }
  }
  // Regenerar el xlsx sin imágenes (openpyxl igual lo abre porque las
  // referencias en drawings.xml quedan huérfanas pero eso no rompe la lectura
  // de las hojas de datos).
  const lightBlob = await zip.generateAsync({
    type: "blob",
    compression: "DEFLATE",
    compressionOptions: { level: 6 },
    mimeType: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  return {
    lightBlob,
    originalSize: file.size,
    lightSize: lightBlob.size,
    imagenesRemovidas,
  };
}

/**
 * Interpreta un string ingresado por el usuario como monto COP.
 *
 * Reglas (para soportar tanto formato colombiano como americano):
 *   - Si hay múltiples "." y/o ",", el ÚLTIMO separador es el decimal y los demás son miles.
 *     Ejemplo: "149.700.813,50" → 149700813.50  (colombiano)
 *              "149,700,813.50" → 149700813.50  (americano)
 *   - Si hay UN solo separador y los dígitos después son EXACTAMENTE 3, se interpreta
 *     como separador de miles (no decimal). Ejemplo: "149.700" → 149700 (no 149.7).
 *   - Si hay UN solo separador y los dígitos después son 1, 2 o 4+, es decimal.
 *     Ejemplo: "8500.5" → 8500.5, "1234.56" → 1234.56
 */
function parseLandedCOP(raw) {
  if (raw === null || raw === undefined) return 0;
  const s = String(raw).replace(/[^\d.,]/g, "");
  if (!s) return 0;
  const lastDot = s.lastIndexOf(".");
  const lastComma = s.lastIndexOf(",");
  const decIdx = Math.max(lastDot, lastComma);
  if (decIdx < 0) {
    // Solo dígitos
    const n = Number(s);
    return Number.isFinite(n) ? n : 0;
  }
  const separadores = (s.match(/[.,]/g) || []).length;
  const digitosDespues = s.length - decIdx - 1;
  // 1 solo separador con exactamente 3 dígitos después = separador de miles
  if (separadores === 1 && digitosDespues === 3) {
    const enteros = s.replace(/[.,]/g, "");
    const n = Number(enteros);
    return Number.isFinite(n) ? n : 0;
  }
  // Caso normal: el último separador es decimal
  const enteros = s.slice(0, decIdx).replace(/[.,]/g, "");
  const decs = s.slice(decIdx + 1).replace(/[.,]/g, "");
  const n = Number(`${enteros || "0"}.${decs || "0"}`);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Modal wizard de 2 pasos para importar contenedor.
 *
 * Paso 1: sube archivo + fija defaults (TRM, %gastos, %margen mín, %margen sug).
 * Paso 2: revisa tabla producto por producto:
 *   - Puede editar % gastos / % margen mín / % margen sug por fila.
 *   - Ve costo actual vs. nuevo costo/precios y decide si "Aplicar cambio de precio".
 *   - Si aplica cambio y el producto tiene stock, puede "Notificar a vendedores".
 */
export default function ContainerImportWizard({ open, onOpenChange, onSuccess }) {
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [trm, setTrm] = useState(4200);
  // Jun/2026: el % de gastos se eliminó del wizard — los gastos reales van en "Gastos landed" (prorrateo). Siempre 0.
  const pctGastos = 0;
  const [pctMin, setPctMin] = useState(30);
  const [pctSug, setPctSug] = useState(60);
  const [gastosLanded, setGastosLanded] = useState("");
  const [metodoProrrateo, setMetodoProrrateo] = useState("valor");
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0); // 0..100 durante subida (Paso 1)
  const [previewJobStatus, setPreviewJobStatus] = useState(""); // texto para mostrar durante polling
  const [preview, setPreview] = useState(null);
  const [rows, setRows] = useState([]);
  const [result, setResult] = useState(null);
  const [cleaning, setCleaning] = useState(false);
  const [originalNames, setOriginalNames] = useState({}); // sku -> nombre original antes de IA
  const [usarImagenes, setUsarImagenes] = useState(true);
  // Ref para permitir cancelar la subida chunked en curso.
  const cancelUploadRef = useRef(false);
  // Progreso de subida de fotos (Paso 4 nuevo — mar/2026 cola persistente)
  const [progresoFotos, setProgresoFotos] = useState(null);
  const [reintentando, setReintentando] = useState(false);
  const pollRef = useRef(null);
  // Edición inline de grupos en el paso 2 (mar/2026): abre popover de una fila
  const [grupoPopoverIdx, setGrupoPopoverIdx] = useState(null);

  const cleanNamesWithAI = async () => {
    if (!preview?.borrador_id || !rows.length) return;
    setCleaning(true);
    try {
      // Guardar los nombres originales por SKU para poder revertir
      const backup = {};
      rows.forEach((r) => { backup[r.sku] = r.nombre; });
      setOriginalNames(backup);
      const nombres = rows.map((r) => r.nombre);
      const { data } = await api.post("/productos/importar-contenedor/limpiar-nombres", {
        nombres,
        borrador_id: preview.borrador_id,
      });
      const limpios = data.nombres_limpios || [];
      setRows((rs) => rs.map((r, i) => ({ ...r, nombre: limpios[i] || r.nombre })));
      toast.success(`IA limpió ${data.resumen?.cambiados || 0} de ${data.resumen?.total || 0} nombres. Puedes editar los que quieras antes de confirmar.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo limpiar con IA. Prueba de nuevo.");
    } finally { setCleaning(false); }
  };

  const revertNames = () => {
    if (!Object.keys(originalNames).length) return;
    setRows((rs) => rs.map((r) => ({ ...r, nombre: originalNames[r.sku] || r.nombre })));
    setOriginalNames({});
    toast.info("Nombres restaurados a los originales del Excel");
  };

  useEffect(() => {
    if (!open) return;
    // Cargar defaults globales al abrir el modal
    api.get("/productos/_/costeo-defaults").then(({ data }) => {
      setTrm(data.trm || 4200);
      setPctMin(data.pct_margen_minimo ?? 30);
      setPctSug(data.pct_margen_sugerido ?? 60);
    }).catch(() => {});
    // Reset al abrir
    setStep(1);
    setFile(null);
    setPreview(null);
    setRows([]);
    setResult(null);
    setProgresoFotos(null);
  }, [open]);

  // Polling del progreso de fotos: se activa cuando result trae borrador_id y
  // se detiene cuando termina o cuando cerramos el modal.
  useEffect(() => {
    const bid = result?.borrador_id;
    if (!bid || !open) {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      return;
    }
    let cancelled = false;
    const fetchProgreso = async () => {
      try {
        const { data } = await api.get(`/productos/importar-contenedor/progreso-fotos/${bid}`);
        if (cancelled) return;
        setProgresoFotos(data);
        if (data.done) {
          if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
          // Invalidar cache del frontend: refresca la lista de productos
          onSuccess?.();
          try {
            const evt = new CustomEvent("beem:productos-cache-invalidated", { detail: { source: "contenedor-fotos" } });
            window.dispatchEvent(evt);
          } catch {}
        }
      } catch {}
    };
    fetchProgreso();
    pollRef.current = setInterval(fetchProgreso, 2000);
    return () => {
      cancelled = true;
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    };
  }, [result?.borrador_id, open]);

  const reintentarFotosFaltantes = async () => {
    const ref = result?.contenedor;
    if (!ref) return;
    setReintentando(true);
    try {
      const { data } = await api.post(`/productos/importar-contenedor/reintentar-fotos/${encodeURIComponent(ref)}`);
      if (data?.encoladas > 0 && data?.borrador_id) {
        setResult((prev) => prev ? { ...prev, borrador_id: data.borrador_id, fotos_en_proceso: data.encoladas } : prev);
        setProgresoFotos(null);
        toast.success(`Reintentando ${data.encoladas} fotos faltantes`);
      } else {
        toast.info(data?.mensaje || "No hay fotos que reintentar");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo reintentar");
    } finally {
      setReintentando(false);
    }
  };

  const recalcRow = (row, pg, pmin, psug) => {
    const base = Number(row.costo_base_cop) || 0;
    const costoReal = Math.round(base * (1 + (Number(pg) || 0) / 100));
    const pMin = Math.round(costoReal * (1 + (Number(pmin) || 0) / 100));
    let pSug = Math.round(costoReal * (1 + (Number(psug) || 0) / 100));
    if (pSug < pMin) pSug = pMin;
    const cambia = row.actual && (
      Math.round(row.actual.costo || 0) !== costoReal ||
      Math.round(row.actual.precio_sugerido || 0) !== pSug ||
      Math.round(row.actual.precio_minimo || 0) !== pMin
    );
    return {
      ...row,
      pct_gastos: Number(pg),
      pct_margen_minimo: Number(pmin),
      pct_margen_sugerido: Number(psug),
      costo_real: costoReal,
      precio_minimo: pMin,
      precio_sugerido: pSug,
      cambia_precio: !!cambia,
    };
  };

  const uploadAndPreview = async () => {
    if (!file) return;
    setLoading(true);
    setUploadProgress(0);
    setPreviewJobStatus("Preparando archivo…");
    // Feb/2026: si el usuario NO va a usar las fotos del archivo (toggle en Paso 1)
    // y el archivo es grande, extraemos las imágenes en el navegador ANTES de
    // subir para reducir el payload. Si SÍ va a usar las fotos, subimos el archivo
    // completo (chunked+paralelo) para que el backend pueda extraerlas.
    let uploadFile = file;
    let ligeroInfo = null;
    if (!usarImagenes && file.size > 10 * 1024 * 1024) {
      try {
        setPreviewJobStatus("Optimizando archivo (modo rápido sin imágenes)…");
        const info = await generarXlsxLigero(file);
        if (info.imagenesRemovidas > 0 && info.lightSize < info.originalSize * 0.5) {
          uploadFile = new File(
            [info.lightBlob],
            file.name.replace(/\.xlsx$/i, "_ligero.xlsx"),
            { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" },
          );
          ligeroInfo = info;
          const ratio = ((info.lightSize / info.originalSize) * 100).toFixed(1);
          toast.success(
            `Archivo optimizado: ${(info.originalSize / (1024*1024)).toFixed(1)} MB → ${(info.lightSize / (1024*1024)).toFixed(1)} MB (${ratio}%). Removimos ${info.imagenesRemovidas} imágenes.`,
            { duration: 6000 },
          );
        }
      } catch (e) {
        // Si falla la optimización, seguimos con el archivo original.
        // eslint-disable-next-line no-console
        console.warn("[wizard] Optimización falló, subiendo archivo original:", e);
      }
    }
    const sizeMB = uploadFile.size / (1024 * 1024);
    try {
      // Feb/2026: para archivos grandes (>2MB) usamos chunked upload.
      // Cada chunk es un request pequeño → nunca topa límites de ingress/proxy.
      // Chunks se suben en PARALELO (concurrencia 6) para maximizar throughput.
      const USE_CHUNKED = uploadFile.size > 2 * 1024 * 1024;
      const CHUNK_SIZE = 4 * 1024 * 1024; // 4MB por chunk
      const CONCURRENCY = 6; // 6 uploads simultáneos (HTTP/2 multiplexa bien)
      cancelUploadRef.current = false; // reset cancelación
      let init = null;
      if (USE_CHUNKED) {
        // Generar upload_id único (crypto.randomUUID o fallback)
        const uploadId = (typeof crypto !== "undefined" && crypto.randomUUID)
          ? crypto.randomUUID().replace(/-/g, "")
          : `up_${Date.now()}_${Math.random().toString(36).slice(2, 12)}`;
        const totalChunks = Math.ceil(uploadFile.size / CHUNK_SIZE);
        setPreviewJobStatus(`Subiendo archivo en ${totalChunks} partes de 4 MB (${CONCURRENCY} en paralelo)…`);
        // Función que sube UN chunk con retry
        const startTime = Date.now();
        let completados = 0;
        const uploadChunk = async (i) => {
          const start = i * CHUNK_SIZE;
          const end = Math.min(start + CHUNK_SIZE, uploadFile.size);
          const chunkBlob = uploadFile.slice(start, end);
          const cfd = new FormData();
          cfd.append("upload_id", uploadId);
          cfd.append("chunk_index", String(i));
          cfd.append("total_chunks", String(totalChunks));
          cfd.append("chunk", chunkBlob, `chunk_${i}.bin`);
          let intento = 0;
          const MAX_INT = 4;
          let ultimoDetalle = "";
          while (true) {
            intento += 1;
            try {
              await api.post(
                "/productos/importar-contenedor/preview/upload-chunk",
                cfd,
                {
                  headers: { "Content-Type": "multipart/form-data" },
                  timeout: 5 * 60 * 1000,
                },
              );
              completados += 1;
              const pct = Math.round((completados / totalChunks) * 100);
              setUploadProgress(pct);
              const elapsed = (Date.now() - startTime) / 1000;
              const speed = elapsed > 0 ? (completados * CHUNK_SIZE / (1024 * 1024) / elapsed).toFixed(1) : "?";
              const eta = completados > 0 ? Math.round((totalChunks - completados) * elapsed / completados) : 0;
              setPreviewJobStatus(`Subiendo ${completados}/${totalChunks} partes · ${speed} MB/s · ~${eta}s restantes`);
              return;
            } catch (chunkErr) {
              const isNet = !chunkErr?.response && (
                chunkErr?.message === "Network Error" ||
                chunkErr?.code === "ERR_NETWORK" ||
                chunkErr?.code === "ECONNRESET" ||
                chunkErr?.code === "ECONNABORTED"
              );
              const status = chunkErr?.response?.status;
              ultimoDetalle = chunkErr?.response?.data?.detail || chunkErr?.message || chunkErr?.code || "sin detalle";
              // eslint-disable-next-line no-console
              console.warn(`[chunked-upload] chunk ${i + 1}/${totalChunks} intento ${intento} falló:`, {
                status, code: chunkErr?.code, message: chunkErr?.message, detail: ultimoDetalle,
              });
              const puedeReintentar = isNet || status === 502 || status === 503 || status === 504 || status === 524;
              if (!puedeReintentar || intento >= MAX_INT) {
                throw new Error(`Chunk ${i + 1}/${totalChunks} falló después de ${intento} intentos${status ? ` (HTTP ${status})` : ""}: ${ultimoDetalle}`);
              }
              await new Promise((r) => setTimeout(r, 1500 * intento));
            }
          }
        };
        // Ejecutar con concurrencia usando un pool simple
        const indices = Array.from({ length: totalChunks }, (_, i) => i);
        const errores = [];
        const runners = Array.from({ length: Math.min(CONCURRENCY, totalChunks) }, async () => {
          while (indices.length > 0 && errores.length === 0) {
            if (cancelUploadRef.current) {
              throw new Error("Subida cancelada por el usuario");
            }
            const i = indices.shift();
            if (i === undefined) return;
            try {
              await uploadChunk(i);
            } catch (e) {
              errores.push(e);
              throw e;
            }
          }
        });
        try {
          await Promise.all(runners);
        } catch (e) {
          throw errores[0] || e;
        }
        // Todos los chunks subidos → iniciar el preview
        setPreviewJobStatus("Ensamblando archivo en el servidor…");
        const ffd = new FormData();
        ffd.append("upload_id", uploadId);
        ffd.append("filename", file.name);
        ffd.append("total_chunks", String(totalChunks));
        ffd.append("trm_cop_por_usd", trm);
        ffd.append("pct_gastos", pctGastos);
        ffd.append("pct_margen_minimo", pctMin);
        ffd.append("pct_margen_sugerido", pctSug);
        ffd.append("gastos_landed_total_cop", parseLandedCOP(gastosLanded));
        ffd.append("metodo_prorrateo", metodoProrrateo);
        // Feb/2026: la finalize ahora retorna en <1s (assembling en background).
        // Si aún falla por red, reintentamos.
        let intentoFin = 0;
        const MAX_FIN = 3;
        while (!init && intentoFin < MAX_FIN) {
          intentoFin += 1;
          try {
            const { data } = await api.post(
              "/productos/importar-contenedor/preview/iniciar-chunked",
              ffd,
              { headers: { "Content-Type": "multipart/form-data" }, timeout: 60 * 1000 },
            );
            init = data;
          } catch (finErr) {
            // eslint-disable-next-line no-console
            console.warn("[chunked-upload] finalize intento", intentoFin, "falló:", finErr);
            const isNet = !finErr?.response && (
              finErr?.message === "Network Error" ||
              finErr?.code === "ERR_NETWORK" ||
              finErr?.code === "ECONNRESET" ||
              finErr?.code === "ECONNABORTED"
            );
            const status = finErr?.response?.status;
            const puedeReintentar = isNet || status === 502 || status === 503 || status === 504 || status === 524;
            if (!puedeReintentar || intentoFin >= MAX_FIN) {
              const detail = finErr?.response?.data?.detail || finErr?.message || "sin detalle";
              throw new Error(`Falló el paso de ensamblado tras ${intentoFin} intentos${status ? ` (HTTP ${status})` : ""}: ${detail}`);
            }
            setPreviewJobStatus(`Ensamblado — reintentando (${intentoFin}/${MAX_FIN})…`);
            await new Promise((r) => setTimeout(r, 2000 * intentoFin));
          }
        }
      } else {
        // Archivo pequeño: subida single-shot (compatibilidad + más rápido).
        const fd = new FormData();
        fd.append("file", uploadFile);
        fd.append("trm_cop_por_usd", trm);
        fd.append("pct_gastos", pctGastos);
        fd.append("pct_margen_minimo", pctMin);
        fd.append("pct_margen_sugerido", pctSug);
        fd.append("gastos_landed_total_cop", parseLandedCOP(gastosLanded));
        fd.append("metodo_prorrateo", metodoProrrateo);
        let intentoSubida = 0;
        const MAX_INTENTOS_SUBIDA = 3;
        let ultimoErrorSubida = null;
        while (intentoSubida < MAX_INTENTOS_SUBIDA && !init) {
          intentoSubida += 1;
          if (intentoSubida > 1) {
            setPreviewJobStatus(`La subida falló. Reintentando (intento ${intentoSubida}/${MAX_INTENTOS_SUBIDA})…`);
            await new Promise((r) => setTimeout(r, 2000 * intentoSubida));
          }
          try {
            const { data } = await api.post(
              "/productos/importar-contenedor/preview/iniciar",
              fd,
              {
                headers: { "Content-Type": "multipart/form-data" },
                timeout: 10 * 60 * 1000,
                onUploadProgress: (evt) => {
                  if (evt.total) setUploadProgress(Math.round((evt.loaded / evt.total) * 100));
                },
              },
            );
            init = data;
          } catch (upErr) {
            ultimoErrorSubida = upErr;
            const isNet = !upErr?.response && (
              upErr?.message === "Network Error" ||
              upErr?.code === "ERR_NETWORK" ||
              upErr?.code === "ECONNRESET" ||
              upErr?.code === "ECONNABORTED"
            );
            const status = upErr?.response?.status;
            const canRetry = isNet || status === 502 || status === 503 || status === 504 || status === 524;
            if (!canRetry) throw upErr;
            if (intentoSubida >= MAX_INTENTOS_SUBIDA) throw upErr;
          }
        }
        if (!init) throw ultimoErrorSubida || new Error("No se pudo subir el archivo");
      }
      const jobId = init?.job_id;
      if (!jobId) throw new Error("No se recibió job_id del servidor");
      setUploadProgress(100);
      setPreviewJobStatus("Procesando el archivo… esto puede tomar hasta 2 minutos si es un archivo grande.");
      // Polling: 3s de intervalo, hasta 10 min máximo.
      // Feb/2026 fix: los polls pueden fallar por red intermitente (proxy,
      // wifi, throttling). Antes 1 poll fallido mataba todo el flujo con
      // "Network Error". Ahora reintentamos hasta 5 fallos consecutivos
      // (~15s de gracia) antes de rendirnos.
      const started = Date.now();
      const MAX_MS = 10 * 60 * 1000;
      const MAX_ERRORES_CONSECUTIVOS = 5;
      let erroresPoll = 0;
      let data = null;
      while (Date.now() - started < MAX_MS) {
        await new Promise((r) => setTimeout(r, 3000));
        try {
          const { data: st } = await api.get(`/productos/importar-contenedor/preview/status/${jobId}`);
          erroresPoll = 0; // reset al primer poll exitoso
          if (!st.procesando) {
            if (st.error) throw new Error(st.error);
            data = st.resultado;
            break;
          }
        } catch (pollErr) {
          // Si es un error de red / timeout: reintentar. Si es un error
          // "real" del backend (con status 4xx/5xx y detail), interrumpir.
          const status = pollErr?.response?.status;
          const detail = pollErr?.response?.data?.detail;
          const isNetwork = !pollErr?.response && (
            pollErr?.message === "Network Error" ||
            pollErr?.code === "ERR_NETWORK" ||
            pollErr?.code === "ECONNABORTED"
          );
          if (isNetwork || status === 502 || status === 503 || status === 504 || status === 524) {
            erroresPoll += 1;
            setPreviewJobStatus(`Red intermitente… reintentando (${erroresPoll}/${MAX_ERRORES_CONSECUTIVOS})`);
            if (erroresPoll >= MAX_ERRORES_CONSECUTIVOS) {
              throw new Error("La conexión se cortó varias veces mientras procesábamos el archivo. Revisa tu red e inténtalo de nuevo.");
            }
            // seguir loop
          } else {
            // Error real del backend con status: propagar mensaje
            throw new Error(detail || pollErr?.message || "Error al procesar el archivo");
          }
        }
      }
      if (!data) throw new Error("El procesamiento tomó demasiado tiempo. Intenta con un archivo más pequeño o dividido.");
      setPreview(data);
      setRows(data.rows || []);
      setStep(2);
    } catch (e) {
      // Feb/2026: mensajes específicos según el tipo de fallo.
      // Si el usuario canceló, no mostramos error alarmante.
      if (e?.message === "Subida cancelada por el usuario") {
        toast.info("Subida cancelada. Marca 'modo rápido' abajo si quieres subir sin las fotos.");
        return;
      }
      let msg = e?.response?.data?.detail || e?.message || "No se pudo analizar el archivo";
      const isNetwork = !e?.response && (
        e?.message === "Network Error" ||
        e?.code === "ERR_NETWORK" ||
        e?.code === "ECONNRESET"
      );
      if (isNetwork) {
        // Solo aplica al path single-shot (archivos <2MB). Chunked genera su propio mensaje.
        const sizeInfo = file?.size ? ` (${(file.size / (1024*1024)).toFixed(1)} MB)` : "";
        msg = `La conexión se cortó al subir el archivo${sizeInfo}. Revisa tu red e inténtalo de nuevo.`;
      }
      // eslint-disable-next-line no-console
      console.error("[uploadAndPreview] error:", e);
      toast.error(msg, { duration: 15000 });
    } finally {
      setLoading(false);
      setUploadProgress(0);
      setPreviewJobStatus("");
    }
  };

  const applyGlobalRecalc = () => {
    setRows((rs) => rs.map((r) => recalcRow(r, pctGastos, pctMin, pctSug)));
    toast.success("Recalculado con los porcentajes globales");
  };

  // ============================================================
  // Edición de grupos/familias en el paso 2 (mar/2026)
  // Permite renombrar el grupo desde la tabla, mover una fila a otro
  // grupo, o crear un grupo nuevo — igual que el paso 3 pero inline.
  // ============================================================
  const grupoKeyDe = (r) => (r?.familia_key_final || r?.familia_key_sugerida || `_solo_${r?.sku || ""}`).toString().toUpperCase();
  const grupoLabelDe = (r) => {
    if (r?.nombre_familia_override) return r.nombre_familia_override;
    if (r?.familia_existente?.nombre_representativo) return r.familia_existente.nombre_representativo;
    const nom = (r?.nombre || "").split(" - ")[0];
    return nom || grupoKeyDe(r);
  };

  // Lista distinct de grupos con al menos 1 fila (para el popover "Mover a...")
  const gruposUnicos = () => {
    const m = new Map();
    rows.forEach((r, i) => {
      const key = grupoKeyDe(r);
      if (!m.has(key)) m.set(key, { key, label: grupoLabelDe(r), count: 0, indices: [] });
      const g = m.get(key);
      g.count += 1;
      g.indices.push(i);
      // Si algún row del grupo trae nombre_familia_override, úsalo como label preferido
      if (r.nombre_familia_override) g.label = r.nombre_familia_override;
    });
    return Array.from(m.values()).sort((a, b) => a.label.localeCompare(b.label));
  };

  // Renombra el grupo de una fila. Aplica el nombre a TODAS las filas del
  // mismo grupo para que quede coherente.
  const renombrarGrupoDeFila = (rowIdx, nuevoNombre) => {
    const origen = rows[rowIdx];
    if (!origen) return;
    const key = grupoKeyDe(origen);
    setRows((rs) => rs.map((r) => grupoKeyDe(r) === key ? { ...r, nombre_familia_override: nuevoNombre } : r));
  };

  // Mueve la fila a un grupo existente (por key). Resetea override de foto.
  const moverFilaAGrupo = (rowIdx, targetKey, targetLabel) => {
    setRows((rs) => rs.map((r, i) => i === rowIdx
      ? {
          ...r,
          familia_key_final: targetKey,
          familia_key_sugerida: targetKey,
          nombre_familia_override: targetLabel || r.nombre_familia_override,
          // Al moverlo a otro grupo, reseteamos las decisiones de foto para
          // que el paso 3 (o el confirmar) las vuelva a resolver.
          foto_fuente: null,
          foto_heredar_de_producto_id: null,
        }
      : r
    ));
    setGrupoPopoverIdx(null);
    toast.success(`Movido a "${targetLabel || targetKey}"`);
  };

  // Crea un grupo nuevo y mueve la fila hacia él.
  const crearGrupoConFila = (rowIdx, nombreNuevo) => {
    const nombre = (nombreNuevo || "").trim();
    if (!nombre) { toast.error("Escribe el nombre del grupo"); return; }
    const nuevaKey = nombre.toUpperCase();
    // Verificar que no sea igual a un grupo existente
    if (gruposUnicos().some((g) => g.key === nuevaKey)) {
      toast.error("Ya existe un grupo con ese nombre. Usa 'Mover a' en su lugar.");
      return;
    }
    moverFilaAGrupo(rowIdx, nuevaKey, nombre);
  };

  const sacarFilaAGrupoPropio = (rowIdx) => {
    // Crea un grupo único basado en el SKU (queda solo)
    const r = rows[rowIdx];
    if (!r) return;
    const nombre = (r.nombre || "").split(" - ")[0] || r.sku;
    moverFilaAGrupo(rowIdx, `SOLO_${r.sku}`.toUpperCase(), nombre);
  };

  const confirm = async (permitirReimportacion = false) => {
    // Prevenir doble-click: si ya está en curso, ignorar clicks adicionales
    if (loading) return;
    setLoading(true);
    try {
      const payload = {
        borrador_id: preview.borrador_id,
        usar_imagenes: !!usarImagenes,
        permitir_reimportacion: !!permitirReimportacion,
        rows: rows.map((r) => ({
          sku: r.sku, nombre: r.nombre, unidad: r.unidad,
          cantidad: r.cantidad,
          cajas: r.cajas,
          unidades_por_caja: r.unidades_por_caja,
          packing_unit: r.packing_unit,
          costo_usd_unit: r.costo_usd_unit || 0,
          costo_base_cop: r.costo_base_cop,
          landed_unit_cop: r.landed_unit_cop || 0,
          pct_gastos: r.pct_gastos,
          pct_margen_minimo: r.pct_margen_minimo,
          pct_margen_sugerido: r.pct_margen_sugerido,
          costo_real: r.costo_real,
          precio_minimo: r.precio_minimo,
          precio_sugerido: r.precio_sugerido,
          existe: r.existe,
          producto_id: r.producto_id,
          aplicar_cambio_precio: !!r.aplicar_cambio_precio,
          notificar_vendedores: !!r.notificar_vendedores,
          agrupar_como_variante: !!r.agrupar_como_variante,
          es_variante_de_id: r.agrupar_como_variante ? (r.padre_sugerido?.id || null) : null,
          variante_label: r.agrupar_como_variante ? (r.variante_label || r.padre_sugerido?.variante_label_sugerida || null) : null,
          imagen_key: r.imagen_key || null,
          imagen_content_type: r.imagen_content_type || null,
          color: r.color || null,
          // Feb/2026: código físico del proveedor (CLIENT'S REF, ej. "EF26-172")
          // — el que va marcado en las cajas del packing list. Es lo que ve el
          // equipo de operaciones al empacar. El SKU sigue siendo único por
          // variante (ANCLAD REF).
          codigo: r.familia_ref_excel || r.familia_key_final || r.familia_key_sugerida || null,
          // Feb/2026: toggle del paso 3 — cuando existe conflicto de código
          // (existente ya tiene un código distinto), este flag decide si el
          // producto se actualiza al nuevo (True) o se mantiene el actual (False).
          // El lote SIEMPRE guarda el nuevo código, sin importar este toggle.
          aplicar_cambio_codigo: !!r.aplicar_cambio_codigo,
          // Overrides del paso 3 "Agrupar por familia":
          familia_key_sugerida: r.familia_key_sugerida || null,
          familia_key_final: r.familia_key_final || null,
          nombre_familia_override: r.nombre_familia_override || null,
          foto_fuente: r.foto_fuente || null,
          foto_heredar_de_producto_id: r.foto_heredar_de_producto_id || null,
          // Jun/2026: fusión SOLO explícita (checkbox en la revisión final)
          fusion_producto_id: (!r.existe && r.fusion_producto_id) ? r.fusion_producto_id : null,
        })),
      };
      const { data } = await api.post("/productos/importar-contenedor/confirmar", payload, {
        // Timeout generoso — Cloudflare corta a 120s pero mantenemos el
        // request abierto para archivos medianos. Para archivos grandes
        // el backend puede devolver {procesando:true} y hacemos polling.
        timeout: 150000,
      });

      // ---------- Fast path: el backend ya terminó ----------
      let finalData = data;

      // ---------- Slow path: aún procesando → polling ----------
      // El backend nos dice "procesando: true" cuando otro request paralelo
      // (o el mismo request tras timeout de Cloudflare) sigue confirmando.
      if (data?.procesando) {
        toast.message("Contenedor grande detectado. Procesando en segundo plano…");
        for (let i = 0; i < 60; i++) { // hasta 5 min (60 * 5s)
          await new Promise((res) => setTimeout(res, 5000));
          try {
            const { data: st } = await api.get(`/productos/importar-contenedor/confirmar/status/${preview.borrador_id}`);
            if (st?.procesando === false && st?.resultado) {
              finalData = st.resultado;
              break;
            }
          } catch { /* seguir intentando */ }
        }
        if (!finalData?.contenedor) {
          throw new Error("El contenedor sigue procesándose. Refresca la página en 1-2 minutos para ver el resultado.");
        }
      }

      setResult(finalData);
      const totalUpd = (finalData.actualizados_solo_stock || 0) + (finalData.actualizados_con_precio || 0);
      let msg = `Contenedor ${finalData.contenedor}: ${finalData.creados} nuevos, ${totalUpd} actualizados`;
      if (finalData.fotos_en_proceso > 0) {
        msg += `. Subiendo ${finalData.fotos_en_proceso} fotos… (verás el progreso ahora)`;
      } else if (finalData.fotos_error) {
        msg += `. Advertencia: no se pudieron preparar las fotos (${finalData.fotos_error}). Usa 'Reintentar' en unos segundos.`;
      }
      toast.success(msg);
      onSuccess?.();
    } catch (e) {
      // Jun/2026: la referencia ya tiene lotes → pedir confirmación explícita
      const status0 = e?.response?.status;
      const det = e?.response?.data?.detail;
      if (status0 === 409 && det?.codigo === "REFERENCIA_YA_IMPORTADA") {
        setLoading(false);
        const ok = window.confirm(`${det.mensaje}\n\nSi es una re-importación por error, cancela y elimina primero el contenedor anterior (eso borra sus lotes).\n\n¿Sumar de todas formas?`);
        if (ok) return confirm(true);
        return;
      }
      // Detección amigable del timeout de Cloudflare (524 / ECONNABORTED)
      // y de errores puros de red (conexión cortada por el proxy sin status).
      const status = e?.response?.status;
      const isTimeout = e?.code === "ECONNABORTED" || status === 524 || status === 504;
      // "Network Error" de axios = fallo de red sin respuesta (proxy cortó, TCP reset, etc.)
      // En este caso el backend probablemente sí procesó el request → fallback a polling.
      const isNetworkError = !e?.response && (
        e?.message === "Network Error" ||
        e?.code === "ERR_NETWORK" ||
        e?.code === "ECONNRESET"
      );
      if (isTimeout || isNetworkError) {
        // Intentar polling — probablemente el backend siguió corriendo
        toast.message(
          isNetworkError
            ? "La conexión se cortó pero el contenedor puede estar procesándose. Verificando…"
            : "El proxy cortó la conexión, pero el contenedor sigue procesándose. Verificando…"
        );
        try {
          for (let i = 0; i < 60; i++) {
            await new Promise((res) => setTimeout(res, 5000));
            try {
              const { data: st } = await api.get(`/productos/importar-contenedor/confirmar/status/${preview.borrador_id}`);
              if (st?.procesando === false && st?.resultado) {
                setResult(st.resultado);
                toast.success(`Contenedor ${st.resultado.contenedor}: ${st.resultado.creados} nuevos.`);
                onSuccess?.();
                return;
              }
            } catch {
              // Un poll puede fallar por la misma razón que el request original;
              // seguimos intentando en el siguiente ciclo.
            }
          }
          toast.error("El contenedor sigue procesándose. Refresca la página en 1-2 minutos y verifica en Productos → Contenedores si ya se creó.");
        } catch {
          toast.error("El servidor tardó demasiado. Refresca en unos minutos y verifica en Productos → Contenedores si ya se creó.");
        }
      } else {
        toast.error(e?.response?.data?.detail || e?.message || "No se pudo confirmar");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!loading) onOpenChange(v); }}>
      <DialogContent className="max-w-[98vw] w-[98vw] max-h-[95vh] overflow-hidden flex flex-col" data-testid="container-wizard" onEscapeKeyDown={(e) => { if (loading) e.preventDefault(); }} onPointerDownOutside={(e) => { if (loading) e.preventDefault(); }}>
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <FileSpreadsheet className="w-6 h-6 text-primary" /> Importar contenedor · Paso {result ? 4 : step} de {result ? 4 : 3}
            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 font-semibold uppercase" title="Versión con chunked upload (archivos grandes en trozos de 2MB)">
              v2.9 · ref-badge
            </span>
          </DialogTitle>
        </DialogHeader>

        {/* Paso 1: setup */}
        {step === 1 && !result && (
          <div className="space-y-4 mt-4 overflow-y-auto">
            <div className="p-4 bg-accent border border-primary/20 rounded-lg text-sm">
              <div className="font-semibold text-primary mb-1">📦 Suba el Excel del proveedor tal cual llegó.</div>
              <div className="text-primary">Después verá cada producto con costo calculado y podrá decidir si aplica el cambio de precio o solo suma stock.</div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>TRM (COP/USD)</Label>
                <Input type="number" step="0.01" value={trm} onChange={(e) => setTrm(e.target.value)} className="h-11 mt-1" data-testid="wizard-trm" />
              </div>
              <div>
                <Label>% Margen mín.</Label>
                <Input type="number" step="0.1" value={pctMin} onChange={(e) => setPctMin(e.target.value)} className="h-11 mt-1" data-testid="wizard-pct-min" />
                <div className="text-[10px] text-muted-foreground mt-0.5">Ganancia mínima permitida</div>
              </div>
              <div>
                <Label>% Margen sug.</Label>
                <Input type="number" step="0.1" value={pctSug} onChange={(e) => setPctSug(e.target.value)} className="h-11 mt-1" data-testid="wizard-pct-sug" />
                <div className="text-[10px] text-muted-foreground mt-0.5">Ganancia objetivo</div>
              </div>
            </div>

            {/* Costo landed adicional (opcional) */}
            <div className="p-3 border border-primary/30 bg-primary/5 rounded-lg space-y-2">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-semibold text-sm text-primary">🏗️ Costo landed (opcional)</div>
                  <div className="text-[11px] text-muted-foreground">Gastos totales del contenedor (flete real, impuestos, bodega, etc.) a prorratear entre productos.</div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Gastos landed totales (COP)</Label>
                  <Input
                    type="text"
                    inputMode="decimal"
                    value={gastosLanded}
                    onChange={(e) => {
                      // Aceptamos SOLO dígitos, punto y coma. Preservamos lo que
                      // el usuario tecleó tal cual (no re-formateamos mientras
                      // escribe, así el cursor no salta). La interpretación
                      // numérica se calcula abajo en el preview.
                      const v = e.target.value.replace(/[^\d.,]/g, "");
                      setGastosLanded(v);
                    }}
                    className="h-11 mt-1"
                    placeholder="Ej: 8.500.000,50 o 8500000.50"
                    data-testid="wizard-landed-total"
                  />
                  {/* Preview en vivo del número interpretado */}
                  <div className="text-[11px] mt-1 flex items-center gap-1.5">
                    {(() => {
                      const parsed = parseLandedCOP(gastosLanded);
                      if (!gastosLanded || parsed === 0) {
                        return <span className="text-muted-foreground">Acepta punto o coma como decimal. Los separadores de miles se ignoran.</span>;
                      }
                      return (
                        <>
                          <CheckCircle2 className="w-3 h-3 text-emerald-600 flex-shrink-0" />
                          <span className="text-emerald-700 font-semibold tabular-nums">= {formatCOP(parsed)}</span>
                        </>
                      );
                    })()}
                  </div>
                </div>
                <div>
                  <Label>Método de prorrateo</Label>
                  <select value={metodoProrrateo} onChange={(e) => setMetodoProrrateo(e.target.value)} className="h-11 mt-1 w-full rounded-md border border-input bg-background px-3 text-sm" data-testid="wizard-metodo-prorrateo">
                    <option value="valor">Por valor FOB (mayor costo = más gasto)</option>
                    <option value="unidades">Igual por unidad</option>
                    <option value="volumen">Por volumen (usa CBM si aplica)</option>
                  </select>
                </div>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <Label>Archivo Excel del proveedor</Label>
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      const { data } = await api.get("/productos/importar-contenedor/plantilla", { responseType: "blob" });
                      const url = URL.createObjectURL(new Blob([data]));
                      const a = document.createElement("a");
                      a.href = url;
                      a.download = "plantilla_contenedor_beem.xlsx";
                      document.body.appendChild(a);
                      a.click();
                      a.remove();
                      URL.revokeObjectURL(url);
                      toast.success("Plantilla descargada");
                    } catch (err) {
                      toast.error(err?.response?.data?.detail || "No se pudo descargar la plantilla");
                    }
                  }}
                  className="text-xs text-primary font-semibold hover:underline inline-flex items-center gap-1"
                  data-testid="wizard-descargar-plantilla"
                  title="Descarga una plantilla XLSX con la estructura que espera el wizard"
                >
                  <Download className="w-3.5 h-3.5" /> Descargar plantilla
                </button>
              </div>
              <label className="mt-1 flex items-center gap-3 h-14 px-4 border-2 border-dashed border-border rounded-lg hover:border-slate-400 cursor-pointer bg-card">
                <Upload className="w-5 h-5 text-muted-foreground/70" />
                <div className="flex-1 text-sm text-muted-foreground">
                  {file ? (
                    <span className="font-medium text-foreground flex items-center gap-2">
                      {file.name}
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${file.size > 30 * 1024 * 1024 ? "bg-rose-100 text-rose-700" : file.size > 15 * 1024 * 1024 ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-700"}`}>
                        {(file.size / (1024 * 1024)).toFixed(1)} MB
                      </span>
                    </span>
                  ) : "Seleccionar archivo (.xlsx) ..."}
                </div>
                <input type="file" accept=".xlsx,.xlsm" onChange={(e) => {
                  const f = e.target.files?.[0] || null;
                  setFile(f);
                  // Feb/2026: para archivos MUY grandes (>30 MB), sugerimos
                  // "modo rápido" por defecto. La subida con imágenes puede
                  // tardar 20+ minutos y las fotos se pueden agregar luego.
                  if (f && f.size > 30 * 1024 * 1024) {
                    setUsarImagenes(false);
                    toast.info(
                      `Archivo grande (${(f.size / (1024 * 1024)).toFixed(1)} MB). Sugerimos modo rápido: sube en segundos sin las fotos del Excel. Puedes cambiar la opción abajo si quieres las fotos.`,
                      { duration: 8000 },
                    );
                  } else {
                    setUsarImagenes(true);
                  }
                }} className="hidden" data-testid="wizard-file" />
              </label>
            </div>

            {/* Feb/2026: toggle para elegir subir con imágenes (más lento pero preserva fotos)
                o sin imágenes (mucho más rápido, útil si solo queremos revisar la data). */}
            {file && file.size > 10 * 1024 * 1024 && (
              <div className="rounded-lg border-2 border-primary/30 bg-primary/5 p-3 space-y-2">
                <div className="flex items-start gap-3">
                  <ImageIcon className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                  <div className="flex-1 space-y-2">
                    <div className="text-sm font-semibold text-foreground">Tu archivo pesa {(file.size / (1024 * 1024)).toFixed(1)} MB. ¿Cómo quieres subirlo?</div>
                    <label className="flex items-start gap-2 cursor-pointer p-2 rounded hover:bg-primary/10">
                      <input
                        type="radio"
                        name="modo-subida"
                        checked={usarImagenes}
                        onChange={() => setUsarImagenes(true)}
                        className="mt-0.5 w-4 h-4"
                        data-testid="modo-con-imagenes"
                      />
                      <div className="flex-1 text-xs">
                        <div className="font-semibold text-foreground">Con imágenes del Excel <span className="text-[10px] font-normal text-muted-foreground">(recomendado)</span></div>
                        <div className="text-muted-foreground">Sube el archivo completo (~{Math.ceil(file.size / (1024 * 1024))} MB). Tarda 1-3 min pero podrás usar las fotos del proveedor en cada producto.</div>
                      </div>
                    </label>
                    <label className="flex items-start gap-2 cursor-pointer p-2 rounded hover:bg-primary/10">
                      <input
                        type="radio"
                        name="modo-subida"
                        checked={!usarImagenes}
                        onChange={() => setUsarImagenes(false)}
                        className="mt-0.5 w-4 h-4"
                        data-testid="modo-sin-imagenes"
                      />
                      <div className="flex-1 text-xs">
                        <div className="font-semibold text-foreground">Sin imágenes (modo rápido)</div>
                        <div className="text-muted-foreground">Removemos las fotos del archivo antes de subir. Sube en 5 segundos. Podrás agregar fotos manualmente después desde la ficha de cada producto.</div>
                      </div>
                    </label>
                  </div>
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              {loading ? (
                <>
                  <Button
                    variant="outline"
                    onClick={() => {
                      cancelUploadRef.current = true;
                      toast.info("Cancelando subida…");
                    }}
                    data-testid="wizard-cancel-upload"
                    className="border-rose-300 text-rose-700 hover:bg-rose-50"
                  >
                    Cancelar subida
                  </Button>
                  <Button disabled className="bg-primary/60 text-primary-foreground">
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {uploadProgress > 0 && uploadProgress < 100 ? `Subiendo ${uploadProgress}%…` : "Procesando…"}
                  </Button>
                </>
              ) : (
                <>
                  <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
                  <Button disabled={!file} onClick={uploadAndPreview} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="wizard-analyze">
                    Analizar y previsualizar
                  </Button>
                </>
              )}
            </div>
            {loading && (
              <div className="mt-2 rounded-lg border border-primary/20 bg-primary/5 p-3 text-xs text-muted-foreground space-y-2" data-testid="wizard-progress">
                {uploadProgress > 0 && uploadProgress < 100 && (
                  <>
                    <div className="flex justify-between font-medium text-foreground">
                      <span>Subiendo archivo…</span>
                      <span>{uploadProgress}%</span>
                    </div>
                    <Progress value={uploadProgress} className="h-1.5" />
                  </>
                )}
                {previewJobStatus && (
                  <div className="leading-relaxed">{previewJobStatus}</div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Paso 2: revisar tabla */}
        {step === 2 && !result && (
          <div className="flex-1 flex flex-col overflow-hidden mt-4">
            <div className="grid grid-cols-4 gap-3 mb-3 text-center">
              <StatCard label="Productos nuevos" value={preview?.resumen?.nuevos} color="emerald" />
              <StatCard label="Existentes" value={preview?.resumen?.existentes} color="slate" />
              <StatCard label="Con cambio de precio" value={preview?.resumen?.con_cambio_precio} color="amber" />
              <StatCard label="Unidades totales" value={preview?.resumen?.total_unidades} color="blue" />
            </div>

            {preview?.meta && (
              <div className="mb-3 p-2 bg-muted/40 border border-border rounded text-xs text-muted-foreground flex gap-4 flex-wrap">
                <div><b>Contenedor:</b> {preview.contenedor_ref}</div>
                {preview.meta["INVOICE NO"] && <div><b>Invoice:</b> {preview.meta["INVOICE NO"]}</div>}
                {preview.meta["PUERTO DE ENTREGA"] && <div><b>Puerto:</b> {preview.meta["PUERTO DE ENTREGA"]}</div>}
                {preview.meta["FECHA DE RECEPCION"] && <div><b>Recepción:</b> {preview.meta["FECHA DE RECEPCION"]}</div>}
                <div><b>TRM aplicada:</b> {preview.trm}</div>
              </div>
            )}

            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <Button
                variant="outline" size="sm"
                onClick={cleanNamesWithAI} disabled={cleaning}
                className="border-primary/40 text-primary"
                data-testid="clean-names-ia"
              >
                <Sparkles className="w-3.5 h-3.5 mr-1.5" />
                {cleaning ? "Limpiando con IA..." : "Limpiar nombres con IA"}
              </Button>
              {Object.keys(originalNames).length > 0 && (
                <Button variant="ghost" size="sm" onClick={revertNames} data-testid="revert-names">
                  <Undo2 className="w-3.5 h-3.5 mr-1.5" /> Revertir nombres
                </Button>
              )}
              <span className="text-[11px] text-muted-foreground ml-1">Tip: haz clic en cualquier nombre de la tabla para editarlo.</span>
            </div>

            {rows.some((r) => r.fusion_candidato && !r.existe) && (
              <div className="mb-3 p-2.5 bg-amber-50 dark:bg-amber-900/15 border border-amber-300 rounded-lg text-xs" data-testid="fusion-banner">
                <b className="text-amber-800 dark:text-amber-300">{rows.filter((r) => r.fusion_candidato && !r.existe).length} referencia(s) nueva(s) coinciden en familia y color con productos que ya tienes.</b>
                <span className="text-muted-foreground ml-1">El sistema ya NO las fusiona solo: cada una se creará con su propio SKU, salvo que marques "se sumará a…" en la fila. Ninguna referencia existente cambiará de código.</span>
              </div>
            )}
            {rows.some((r) => r.padre_sugerido && !r.existe) && (
              <div className="mb-3 p-2.5 bg-primary/5 border border-primary/30 rounded-lg text-xs flex items-center gap-3 flex-wrap" data-testid="var-suggest-banner">
                <div className="flex-1 min-w-0">
                  <b className="text-primary">Sugerencia de variantes detectada.</b>
                  <span className="text-muted-foreground ml-1">
                    {rows.filter((r) => r.padre_sugerido && !r.existe).length} SKU(s) nuevos parecen ser variantes de productos ya existentes. Marca cada casilla para agruparlos automáticamente.
                  </span>
                </div>
                <Button size="sm" variant="outline" onClick={() => setRows((rs) => rs.map((r) => (r.padre_sugerido && !r.existe) ? { ...r, agrupar_como_variante: true, variante_label: r.variante_label || r.padre_sugerido.variante_label_sugerida || "" } : r))} data-testid="var-accept-all">
                  Aceptar todas
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setRows((rs) => rs.map((r) => (r.padre_sugerido && !r.existe) ? { ...r, agrupar_como_variante: false } : r))} data-testid="var-reject-all">
                  Rechazar
                </Button>
              </div>
            )}

            {/* Banner de agrupamiento por familia */}
            {preview?.resumen && (preview.resumen.familias_unicas > 0 || preview.resumen.nuevos_en_familia_existente > 0) && (
              <div className="mb-3 p-2.5 bg-primary/5 border border-primary/30 rounded-lg text-xs" data-testid="familia-resumen-banner">
                <div className="flex items-center gap-2 flex-wrap">
                  <span>🎨</span>
                  <span>Este contenedor traerá <b>{preview.resumen.familias_unicas}</b> familias/estilos únicos.</span>
                  {preview.resumen.nuevos_en_familia_existente > 0 && (
                    <span className="text-emerald-800 font-medium">
                      · <b>{preview.resumen.nuevos_en_familia_existente}</b> productos nuevos se agregarán a familias que <b>ya existen</b> en el catálogo (aparecerán agrupados con sus otros colores automáticamente).
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Feb/2026 — Banner CRÍTICO: filas descartadas durante el preview.
                Antes se saltaban silenciosamente las filas sin SKU o con SKU duplicado
                para variantes de color distintas, y el usuario no sabía que esos
                productos nunca entraron al inventario. Ahora las listamos para que
                pueda corregir el Excel y re-importar. */}
            {preview?.filas_descartadas && preview.filas_descartadas.length > 0 && (
              <div className="mb-3 p-3 bg-rose-50 border-2 border-rose-300 rounded-lg text-xs" data-testid="filas-descartadas-banner">
                <div className="flex items-start gap-2 mb-2">
                  <span className="text-base">⚠️</span>
                  <div className="flex-1">
                    <b className="text-rose-800 text-sm">
                      {preview.filas_descartadas.length} fila{preview.filas_descartadas.length === 1 ? "" : "s"} del Excel NO entraron al inventario
                    </b>
                    <div className="text-rose-700 mt-0.5">
                      Revisa el Excel original y corrige los problemas listados abajo. Puedes agregar esos productos manualmente desde el catálogo.
                    </div>
                  </div>
                </div>
                <ul className="pl-6 space-y-0.5 max-h-32 overflow-y-auto text-rose-900">
                  {preview.filas_descartadas.slice(0, 10).map((f, i) => (
                    <li key={i} className="list-disc">
                      <b>Fila {f.row_excel ?? "?"}</b>
                      {f.sku ? ` (SKU "${f.sku}")` : ""}
                      {f.nombre ? ` — "${String(f.nombre).slice(0, 60)}"` : ""}
                      : <span className="italic">{f.razon}</span>
                    </li>
                  ))}
                  {preview.filas_descartadas.length > 10 && (
                    <li className="list-disc text-rose-700">… y {preview.filas_descartadas.length - 10} fila(s) más</li>
                  )}
                </ul>
              </div>
            )}

            {/* Banner de fotos detectadas en el Excel */}
            {preview?.resumen?.imagenes_detectadas > 0 && (
              <div className="mb-3 p-2.5 bg-emerald-50 border border-emerald-200 rounded-lg text-xs flex items-center gap-3 flex-wrap" data-testid="fotos-detectadas-banner">
                <div className="flex-1 min-w-0">
                  <b className="text-emerald-800">Fotos detectadas: {preview.resumen.imagenes_detectadas}</b>
                  <span className="text-muted-foreground ml-1">
                    El documento trae imágenes embebidas. Si activas la casilla, cada foto se guardará
                    en su producto al confirmar (podrás editarla luego desde la ficha del producto).
                  </span>
                </div>
                <label className="flex items-center gap-2 text-xs font-medium cursor-pointer">
                  <input
                    type="checkbox"
                    checked={usarImagenes}
                    onChange={(e) => setUsarImagenes(e.target.checked)}
                    className="w-4 h-4"
                    data-testid="usar-imagenes-check"
                  />
                  <span>Usar las fotos del documento</span>
                </label>
              </div>
            )}

            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <span className="text-xs text-muted-foreground">Recalcular con:</span>
              <Input type="number" step="0.1" value={pctMin} onChange={(e) => setPctMin(e.target.value)} className="h-8 w-20 text-xs" placeholder="Mín" />
              <Input type="number" step="0.1" value={pctSug} onChange={(e) => setPctSug(e.target.value)} className="h-8 w-20 text-xs" placeholder="Sug" />
              <Button variant="outline" size="sm" onClick={applyGlobalRecalc} data-testid="apply-global-recalc">Aplicar a todas</Button>
              <div className="ml-auto flex gap-2">
                <Button variant="outline" size="sm" onClick={() => {
                  setRows((rs) => rs.map((r) => ({ ...r, aplicar_cambio_precio: true })));
                }} data-testid="apply-all-prices">Aplicar todos los cambios</Button>
                <Button variant="outline" size="sm" onClick={() => {
                  setRows((rs) => rs.map((r) => ({ ...r, aplicar_cambio_precio: false })));
                }} data-testid="reject-all-prices">Solo sumar stock (no tocar precios)</Button>
              </div>
            </div>

            <div className="flex-1 overflow-auto border border-border rounded-lg">
              <table className="min-w-full text-xs">
                <thead className="bg-muted sticky top-0 z-10">
                  <tr className="text-left text-[11px] uppercase font-bold text-muted-foreground">
                    <th className="px-3 py-3">SKU</th>
                    <th className="px-3 py-3">Producto</th>
                    <th className="px-3 py-3 text-right">Empaque</th>
                    <th className="px-3 py-3 text-right">Cant. total</th>
                    <th className="px-3 py-3 text-right">USD</th>
                    <th className="px-3 py-3 text-right">Base COP</th>
                    <th className="px-3 py-3 text-right w-20">%Mín</th>
                    <th className="px-3 py-3 text-right w-20">%Sug</th>
                    <th className="px-3 py-3 text-right">Costo real</th>
                    <th className="px-3 py-3 text-right">P.Mín</th>
                    <th className="px-3 py-3 text-right">P.Sug</th>
                    <th className="px-3 py-3">Actual</th>
                    <th className="px-3 py-3 text-center">Precio</th>
                    <th className="px-3 py-3 text-center">Notif.</th>
                    <th className="px-3 py-3 text-center" title="Excluir de la importación (consumo personal, error, etc.)">Excluir</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, idx) => (
                    <tr key={r.sku || `row-${idx}`} className={`border-t border-border/60 ${r.existe && r.cambia_precio ? "bg-amber-50/50" : (!r.existe ? "bg-accent/40" : "")}`} data-testid={`preview-row-${idx}`}>
                      <td className="px-2 py-1 font-mono text-[11px] text-foreground/80">{r.sku}</td>
                      <td className="px-2 py-1 max-w-[320px]">
                        <input
                          value={r.nombre}
                          onChange={(e) => setRows((rs) => rs.map((x, i) => i === idx ? { ...x, nombre: e.target.value } : x))}
                          className="w-full bg-transparent text-foreground text-sm font-medium border-0 border-b border-transparent hover:border-border focus:border-primary focus:outline-none px-0 py-1"
                          title={r.nombre}
                          data-testid={`preview-nombre-${idx}`}
                        />
                        <div className="text-[10px] text-muted-foreground/70">
                          {!r.existe ? <span className="text-primary font-semibold">NUEVO</span> : "Existente"}
                          {r.color ? ` · ${r.color}` : ""}{r.size ? ` · ${r.size}` : ""}
                          {r.filas_origen > 1 && <span className="ml-1 text-blue-600" title={`Consolidadas ${r.filas_origen} filas del Excel`}>· {r.filas_origen} filas</span>}
                          {r.factor_empaque_aplicado > 1 && (
                            <span className="ml-1 px-1 rounded bg-violet-100 text-violet-800 font-semibold" title={`El Excel trae ${r.cantidad_excel} paquetes; el producto vende sub-paquetes (×${r.factor_empaque_aplicado}). Cantidad ×${r.factor_empaque_aplicado} y costo ÷${r.factor_empaque_aplicado}.`} data-testid={`factor-empaque-${idx}`}>
                              desglosado ×{r.factor_empaque_aplicado} ({r.cantidad_excel} paq.)
                            </span>
                          )}
                        </div>
                        {r.fusion_candidato && !r.existe && (
                          <div className={`mt-1 rounded-md border px-1.5 py-1 text-[10px] leading-tight ${r.fusion_producto_id ? "bg-rose-50 border-rose-400" : "bg-amber-50 border-amber-300"}`}
                            data-testid={`fusion-sug-${idx}`}>
                            <label className="flex items-start gap-1 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={!!r.fusion_producto_id}
                                onChange={(e) => setRows((rs) => rs.map((x, i) => i === idx ? { ...x, fusion_producto_id: e.target.checked ? x.fusion_candidato.producto_id : null } : x))}
                                className="w-3 h-3 flex-shrink-0 mt-0.5"
                                data-testid={`fusion-chk-${idx}`}
                              />
                              <span className={r.fusion_producto_id ? "text-rose-700 font-semibold" : "text-amber-800"}>
                                {r.fusion_producto_id ? "SE SUMARÁ a " : "Ya existe un producto del mismo color en esta familia: "}
                                <b>{r.fusion_candidato.nombre}</b> (SKU {r.fusion_candidato.sku}{r.fusion_candidato.codigo ? ` · ${r.fusion_candidato.codigo}` : ""}, stock {r.fusion_candidato.stock_actual}).
                                {!r.fusion_producto_id && <span className="text-muted-foreground"> Si no marcas, se crea como producto NUEVO con su propio SKU {r.sku}.</span>}
                              </span>
                            </label>
                          </div>
                        )}
                        {r.padre_sugerido && !r.existe && (
                          <div className={`mt-1 rounded-md border px-1.5 py-1 text-[10px] leading-tight ${r.agrupar_como_variante ? "bg-primary/10 border-primary/40" : "bg-amber-50 border-amber-200"}`}
                            data-testid={`var-sug-${idx}`}>
                            <div className="flex items-center gap-1">
                              <input
                                type="checkbox"
                                checked={!!r.agrupar_como_variante}
                                onChange={(e) => setRows((rs) => rs.map((x, i) => i === idx ? { ...x, agrupar_como_variante: e.target.checked, variante_label: e.target.checked ? (x.variante_label || x.padre_sugerido?.variante_label_sugerida || "") : "" } : x))}
                                className="w-3 h-3 flex-shrink-0"
                                data-testid={`var-chk-${idx}`}
                              />
                              <span className={r.agrupar_como_variante ? "text-primary font-semibold" : "text-amber-800"}>
                                Variante de <b>{r.padre_sugerido.nombre}</b>
                              </span>
                            </div>
                            {r.agrupar_como_variante && (
                              <input
                                value={r.variante_label || ""}
                                onChange={(e) => setRows((rs) => rs.map((x, i) => i === idx ? { ...x, variante_label: e.target.value } : x))}
                                placeholder="Etiqueta (ej: Rojo #12)"
                                className="mt-1 w-full h-6 border border-primary/40 rounded px-1 text-[10px] bg-card"
                                data-testid={`var-label-${idx}`}
                              />
                            )}
                          </div>
                        )}
                        {/* Badge de familia (agrupamiento por estilo). Se muestra si esta fila
                            se va a agregar a una familia que ya tiene otras variantes en el catalogo. */}
                        {r.familia_existente && !r.existe && (
                          <div className="mt-1 rounded-md border border-emerald-300 bg-emerald-50 px-1.5 py-1 text-[10px] leading-tight text-emerald-800"
                               data-testid={`fam-hit-${idx}`}
                               title={`Ya hay ${r.familia_existente.variantes_count} variantes en esta familia: ${r.familia_existente.colores_ya_registrados.join(', ')}`}>
                            <div className="flex items-center gap-1">
                              <span>🎨</span>
                              <span>Entra a familia con <b>{r.familia_existente.variantes_count}</b> variantes</span>
                            </div>
                            {r.familia_existente.colores_ya_registrados.length > 0 && (
                              <div className="mt-0.5 text-emerald-700/80 truncate max-w-[240px]">
                                Colores ya en catálogo: {r.familia_existente.colores_ya_registrados.slice(0, 5).join(' · ')}
                                {r.familia_existente.colores_ya_registrados.length > 5 ? ` +${r.familia_existente.colores_ya_registrados.length - 5}` : ''}
                              </div>
                            )}
                          </div>
                        )}
                        {/* Editor de grupo (mar/2026): permite renombrar / mover / crear
                            grupo directamente desde el paso 2, sin ir al paso 3. */}
                        <GrupoEditorInline
                          idx={idx}
                          row={r}
                          grupoLabel={grupoLabelDe(r)}
                          grupoKey={grupoKeyDe(r)}
                          countEnGrupo={rows.filter((x) => grupoKeyDe(x) === grupoKeyDe(r)).length}
                          gruposDisponibles={gruposUnicos()}
                          onRenombrar={(nuevo) => renombrarGrupoDeFila(idx, nuevo)}
                          onMoverA={(k, l) => moverFilaAGrupo(idx, k, l)}
                          onCrearNuevo={(nom) => crearGrupoConFila(idx, nom)}
                          onSacar={() => sacarFilaAGrupoPropio(idx)}
                          abierto={grupoPopoverIdx === idx}
                          onToggle={() => setGrupoPopoverIdx(grupoPopoverIdx === idx ? null : idx)}
                          onCerrar={() => setGrupoPopoverIdx(null)}
                        />
                      </td>
                      <td className="px-2 py-1 text-right">
                        {r.cajas && r.unidades_por_caja ? (
                          <div className="text-[10px] text-muted-foreground leading-tight">
                            <div className="tabular-nums">{formatNumber(Math.round(r.cajas))} cajas</div>
                            <div className="text-muted-foreground/70 tabular-nums">× {r.unidades_por_caja} {r.unidad || "u"}/caja</div>
                          </div>
                        ) : r.unidades_por_caja ? (
                          <div className="text-[10px] text-muted-foreground tabular-nums">{r.unidades_por_caja} {r.unidad || "u"}/caja</div>
                        ) : (
                          <div className="text-[10px] text-muted-foreground/70">suelto</div>
                        )}
                        {r.packing_unit && <div className="text-[9px] text-muted-foreground/70 truncate max-w-[80px]" title={r.packing_unit}>{r.packing_unit}</div>}
                      </td>
                      <td className="px-2 py-1 text-right tabular-nums font-semibold">
                        {formatNumber(r.cantidad)}
                        <div className="text-[9px] text-muted-foreground/70 font-normal">{r.unidad || "unid"}</div>
                      </td>
                      <td className="px-2 py-1 text-right tabular-nums">${r.costo_usd_unit?.toFixed?.(2) || "-"}</td>
                      <td className="px-2 py-1 text-right tabular-nums">{formatCOP(r.costo_base_cop)}</td>
                      <td className="px-1 py-1">
                        <input type="number" step="0.1" value={r.pct_margen_minimo}
                          onChange={(e) => setRows((rs) => rs.map((x, i) => i === idx ? recalcRow({ ...x, pct_margen_minimo: e.target.value }, x.pct_gastos, e.target.value, x.pct_margen_sugerido) : x))}
                          className="w-20 h-8 border border-border rounded px-1.5 text-right text-sm" />
                      </td>
                      <td className="px-1 py-1">
                        <input type="number" step="0.1" value={r.pct_margen_sugerido}
                          onChange={(e) => setRows((rs) => rs.map((x, i) => i === idx ? recalcRow({ ...x, pct_margen_sugerido: e.target.value }, x.pct_gastos, x.pct_margen_minimo, e.target.value) : x))}
                          className="w-20 h-8 border border-border rounded px-1.5 text-right text-sm" />
                      </td>
                      <td className="px-2 py-1 text-right tabular-nums font-semibold text-sm">{formatCOP(r.costo_real)}</td>
                      <td className="px-2 py-1 text-right tabular-nums text-amber-700 text-sm">{formatCOP(r.precio_minimo)}</td>
                      <td className="px-2 py-1 text-right tabular-nums text-primary font-semibold text-sm">{formatCOP(r.precio_sugerido)}</td>
                      <td className="px-2 py-1 text-[10px] text-muted-foreground">
                        {r.actual ? (
                          <div className="space-y-0.5">
                            <div>Cost: {formatCOP(r.actual.costo || 0)}</div>
                            <div>Sug: {formatCOP(r.actual.precio_sugerido || 0)}</div>
                            <div>Stk: {formatNumber(r.actual.stock_actual || 0)}</div>
                          </div>
                        ) : "—"}
                      </td>
                      <td className="px-2 py-1 text-center">
                        {r.existe ? (
                          <label className="inline-flex items-center gap-1 cursor-pointer" title="Aplicar cambio de precio">
                            <input type="checkbox" checked={!!r.aplicar_cambio_precio}
                              onChange={(e) => setRows((rs) => rs.map((x, i) => i === idx ? { ...x, aplicar_cambio_precio: e.target.checked, notificar_vendedores: e.target.checked ? x.notificar_vendedores : false } : x))}
                              className="w-4 h-4" data-testid={`chk-precio-${idx}`} />
                            {r.cambia_precio && !r.aplicar_cambio_precio && (
                              <AlertTriangle className="w-3 h-3 text-amber-500" />
                            )}
                          </label>
                        ) : (
                          <span className="text-[10px] text-primary font-semibold">Auto</span>
                        )}
                      </td>
                      <td className="px-2 py-1 text-center">
                        {r.existe && r.aplicar_cambio_precio ? (
                          <input type="checkbox" checked={!!r.notificar_vendedores}
                            onChange={(e) => setRows((rs) => rs.map((x, i) => i === idx ? { ...x, notificar_vendedores: e.target.checked } : x))}
                            className="w-4 h-4" title="Notificar a vendedores" data-testid={`chk-notif-${idx}`} />
                        ) : <span className="text-muted-foreground/50">—</span>}
                      </td>
                      <td className="px-2 py-1 text-center">
                        <button
                          type="button"
                          onClick={() => {
                            if (!window.confirm(`¿Excluir "${r.nombre}" (${r.sku}) de esta importación?\n\nEste producto NO se importará al catálogo. Ideal si fue consumo personal o llegó por error.`)) return;
                            setRows((rs) => rs.filter((_, i) => i !== idx));
                          }}
                          className="p-1 rounded hover:bg-rose-100 text-muted-foreground/70 hover:text-rose-600 transition-colors"
                          title="Excluir esta fila de la importación"
                          data-testid={`row-excluir-${idx}`}
                        >
                          ✕
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex justify-between gap-2 pt-3">
              <Button variant="outline" onClick={() => setStep(1)}>
                <ArrowLeft className="w-4 h-4 mr-2" /> Volver
              </Button>
              <div className="text-xs text-muted-foreground self-center">
                {rows.filter((r) => r.existe && r.aplicar_cambio_precio).length} cambios de precio · {rows.filter((r) => r.notificar_vendedores).length} notificaciones a vendedores
              </div>
              <Button onClick={() => setStep(3)} disabled={loading} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="wizard-next-agrupar">
                Siguiente: agrupar familias
              </Button>
            </div>
          </div>
        )}

        {/* Paso 3: agrupar por familia */}
        {step === 3 && !result && (
          <AgruparFamiliasStep
            rows={rows}
            setRows={setRows}
            onVolver={() => setStep(2)}
            onContinuar={() => confirm(false)}
            loading={loading}
          />
        )}

        {/* Paso 4: resultado + progreso de fotos */}
        {result && (
          <div className="mt-4 space-y-3 overflow-y-auto">
            <div className="flex items-center gap-2 text-primary">
              <CheckCircle2 className="w-6 h-6" />
              <div className="text-lg font-semibold">Contenedor recibido: {result.contenedor}</div>
            </div>
            <div className="grid grid-cols-4 gap-3">
              <StatCard label="Creados" value={result.creados} color="emerald" />
              <StatCard label="Solo stock" value={result.actualizados_solo_stock} color="slate" />
              <StatCard label="Precio actualizado" value={result.actualizados_con_precio} color="amber" />
              <StatCard label="Unidades" value={result.unidades_recibidas} color="blue" />
            </div>
            {result.integridad && (
              <div className={`mt-3 rounded-lg border p-3 text-xs ${result.integridad.ok ? "border-emerald-300 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-800 dark:text-emerald-200" : "border-rose-300 bg-rose-50 dark:bg-rose-900/20 text-rose-800 dark:text-rose-200"}`} data-testid="integridad-panel">
                <b>{result.integridad.ok ? "Verificación de integridad OK" : "Atención: la verificación de integridad no cuadra"}</b>
                <div className="mt-0.5 tabular-nums">
                  Excel {Number(result.integridad.unidades_excel).toLocaleString("es-CO")} und · procesadas {Number(result.integridad.unidades_procesadas).toLocaleString("es-CO")} · en lotes {Number(result.integridad.unidades_lotes).toLocaleString("es-CO")}
                  {result.errores?.length > 0 && ` · ${result.errores.length} fila(s) omitida(s)`}
                </div>
                {!result.integridad.ok && <div className="mt-1">Revisa las filas omitidas abajo. Puedes usar "Conciliar con Excel" en Contenedores importados para dejar los lotes exactos al documento.</div>}
              </div>
            )}

            {/* Panel de subida de fotos (aparece si hay fotos encoladas) */}
            {result.borrador_id && result.fotos_en_proceso > 0 && (
              <FotoProgresoPanel
                progreso={progresoFotos}
                totalEsperado={result.fotos_en_proceso}
                onReintentar={reintentarFotosFaltantes}
                reintentando={reintentando}
              />
            )}

            {/* Banner de error en encolado (Excel aún subiéndose, etc). El
                usuario puede darle a Reintentar en unos segundos. */}
            {!result.borrador_id && result.fotos_error && (
              <div className="p-4 rounded-lg border border-amber-300 bg-amber-50 flex items-start gap-3" data-testid="fotos-error-panel">
                <AlertTriangle className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
                <div className="flex-1 space-y-2 text-sm">
                  <div className="font-semibold text-amber-900">No se pudieron preparar las fotos aún</div>
                  <div className="text-amber-800">{result.fotos_error}</div>
                  <div className="text-xs text-amber-700">Esto suele deberse a que el Excel aún se está subiendo. Espera unos segundos y dale a Reintentar.</div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={reintentarFotosFaltantes}
                    disabled={reintentando}
                    className="mt-1"
                    data-testid="fotos-error-reintentar-btn"
                  >
                    {reintentando ? <Loader2 className="w-3 h-3 mr-2 animate-spin" /> : <RotateCcw className="w-3 h-3 mr-2" />}
                    Reintentar fotos
                  </Button>
                </div>
              </div>
            )}

            {result.errores?.length > 0 && (
              <div className="max-h-40 overflow-auto border border-rose-200 rounded p-2 text-xs text-rose-700 bg-rose-50">
                <div className="font-semibold mb-1">Errores en filas:</div>
                {result.errores.map((e, i) => (
                  <div key={i}>{e.sku}: {e.error}</div>
                ))}
              </div>
            )}
            <div className="flex justify-between items-center">
              <div className="text-xs text-muted-foreground">
                {progresoFotos && !progresoFotos.done && "Puedes cerrar este modal — las fotos siguen subiendo en el fondo."}
              </div>
              <Button onClick={() => onOpenChange(false)} className="bg-primary hover:bg-primary/90" data-testid="wizard-cerrar-btn">Cerrar</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function FotoProgresoPanel({ progreso, totalEsperado, onReintentar, reintentando }) {
  // Si aún no llegó el primer poll, mostramos placeholder con el total esperado
  const total = progreso?.total || totalEsperado || 0;
  const subidas = progreso?.subidas || 0;
  const fallidas = progreso?.fallidas || 0;
  const enProceso = progreso?.en_proceso || 0;
  const pendientes = progreso?.pendientes ?? Math.max(0, total - subidas - fallidas - enProceso);
  const done = progreso?.done;
  const pct = total > 0 ? Math.round((subidas / total) * 100) : 0;
  const skuActual = progreso?.sku_actual;

  return (
    <div className="p-4 rounded-lg border border-primary/30 bg-primary/5 space-y-3" data-testid="foto-progreso-panel">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 font-semibold text-primary">
          <ImageIcon className="w-5 h-5" />
          {done ? "Fotos subidas" : "Subiendo fotos del contenedor"}
          {!done && <Loader2 className="w-4 h-4 animate-spin" />}
        </div>
        <div className="text-sm tabular-nums font-mono">
          <span className="text-primary font-bold">{subidas}</span>
          <span className="text-muted-foreground"> / {total}</span>
        </div>
      </div>
      <Progress value={pct} className="h-3" data-testid="foto-progreso-bar" />
      <div className="grid grid-cols-4 gap-2 text-xs">
        <div className="text-center">
          <div className="tabular-nums font-semibold text-primary">{subidas}</div>
          <div className="text-muted-foreground uppercase text-[10px]">Subidas</div>
        </div>
        <div className="text-center">
          <div className="tabular-nums font-semibold text-blue-700">{enProceso}</div>
          <div className="text-muted-foreground uppercase text-[10px]">En proceso</div>
        </div>
        <div className="text-center">
          <div className="tabular-nums font-semibold text-slate-500">{pendientes}</div>
          <div className="text-muted-foreground uppercase text-[10px]">Pendientes</div>
        </div>
        <div className="text-center">
          <div className={`tabular-nums font-semibold ${fallidas > 0 ? "text-rose-600" : "text-muted-foreground"}`}>{fallidas}</div>
          <div className="text-muted-foreground uppercase text-[10px]">Fallidas</div>
        </div>
      </div>
      {skuActual && !done && (
        <div className="text-xs text-muted-foreground">
          Última: <span className="font-mono font-semibold text-foreground">{skuActual}</span>
        </div>
      )}
      {done && fallidas > 0 && (
        <div className="flex items-center justify-between gap-3 pt-2 border-t border-primary/20">
          <div className="text-xs text-rose-700">
            {fallidas} foto(s) no se pudieron subir después de 3 intentos.
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={onReintentar}
            disabled={reintentando}
            data-testid="foto-reintentar-btn"
          >
            {reintentando ? <Loader2 className="w-3 h-3 mr-2 animate-spin" /> : <RotateCcw className="w-3 h-3 mr-2" />}
            Reintentar faltantes
          </Button>
        </div>
      )}
      {done && fallidas === 0 && (
        <div className="text-xs text-primary font-semibold flex items-center gap-1">
          <CheckCircle2 className="w-4 h-4" /> Todas las fotos quedaron subidas. Ya puedes cerrar este modal.
        </div>
      )}
      {progreso?.fallos?.length > 0 && (
        <details className="text-xs">
          <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
            Ver detalle de fallos ({progreso.fallos.length})
          </summary>
          <div className="mt-2 max-h-32 overflow-auto space-y-1 text-rose-700 bg-rose-50 rounded p-2">
            {progreso.fallos.map((f, i) => (
              <div key={i}>
                <span className="font-mono font-semibold">{f.sku}</span>: {f.error || "error desconocido"}
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

function StatCard({ label, value, color = "slate" }) {
  const map = {
    emerald: "text-primary",
    amber: "text-amber-700",
    slate: "text-foreground",
    blue: "text-blue-700",
  };
  return (
    <div className="p-3 bg-card border border-border rounded-lg text-center">
      <div className={`text-2xl font-semibold tabular-nums ${map[color] || map.slate}`}>{value !== undefined ? formatNumber(value) : "—"}</div>
      <div className="text-[10px] uppercase text-muted-foreground font-bold mt-0.5">{label}</div>
    </div>
  );
}

/**
 * GrupoEditorInline (mar/2026) — mini-editor de grupo por fila en el paso 2
 * del wizard de contenedor. Permite:
 *   - Renombrar el grupo/familia (aplica a todas las filas del mismo grupo).
 *   - Mover esta fila a otro grupo existente.
 *   - Crear un grupo nuevo con esta fila.
 *   - Sacar la fila del grupo (la deja sola).
 *
 * Diseño compacto (2 líneas máx) para no romper la tabla actual del paso 2.
 */
function GrupoEditorInline({
  idx, row, grupoLabel, grupoKey, countEnGrupo, gruposDisponibles,
  onRenombrar, onMoverA, onCrearNuevo, onSacar,
  abierto, onToggle, onCerrar,
}) {
  const [nombreLocal, setNombreLocal] = useState(grupoLabel);
  const [nombreNuevo, setNombreNuevo] = useState("");
  const [busquedaGrupo, setBusquedaGrupo] = useState("");

  // Sincronizar el input local cuando cambia el label desde afuera
  useEffect(() => { setNombreLocal(grupoLabel); }, [grupoLabel]);

  // Al cerrar el popover, limpiar la búsqueda para el próximo uso
  useEffect(() => { if (!abierto) setBusquedaGrupo(""); }, [abierto]);

  const commitRename = () => {
    const val = (nombreLocal || "").trim();
    if (val && val !== grupoLabel) onRenombrar(val);
  };

  return (
    <div className="mt-1 relative" data-testid={`grupo-editor-${idx}`}>
      <div className="flex items-center gap-1 text-[10px] leading-tight rounded-md border border-blue-200 bg-blue-50/60 px-1.5 py-1">
        <span className="text-blue-700 font-semibold flex-shrink-0">Grupo:</span>
        <input
          value={nombreLocal}
          onChange={(e) => setNombreLocal(e.target.value)}
          onBlur={commitRename}
          onKeyDown={(e) => {
            if (e.key === "Enter") { e.preventDefault(); commitRename(); e.target.blur(); }
            if (e.key === "Escape") { setNombreLocal(grupoLabel); e.target.blur(); }
          }}
          className="flex-1 min-w-0 bg-transparent border-0 border-b border-transparent hover:border-blue-300 focus:border-blue-500 focus:outline-none px-0 py-0 text-blue-900 font-medium text-[11px]"
          data-testid={`grupo-nombre-${idx}`}
          placeholder="Nombre del grupo"
        />
        {countEnGrupo > 1 && (
          <span className="text-blue-600/70 flex-shrink-0" title={`${countEnGrupo} ítems en este grupo`}>×{countEnGrupo}</span>
        )}
        <button
          type="button"
          onClick={onToggle}
          className="flex-shrink-0 px-1 rounded hover:bg-blue-200/60 text-blue-700"
          title="Cambiar de grupo"
          data-testid={`grupo-mover-btn-${idx}`}
        >
          ⋯
        </button>
      </div>
      {abierto && (
        <>
          {/* Backdrop transparente para cerrar al clic fuera */}
          <div className="fixed inset-0 z-30" onClick={onCerrar} />
          <div className="absolute z-40 mt-1 left-0 w-72 rounded-lg border border-border bg-card shadow-xl p-2 text-xs space-y-2"
               data-testid={`grupo-popover-${idx}`}>
            <div className="font-semibold text-foreground border-b border-border pb-1">
              Cambiar grupo de <span className="font-mono text-primary">{row.sku}</span>
            </div>
            {/* Buscador para filtrar la lista de grupos destino */}
            <input
              value={busquedaGrupo}
              onChange={(e) => setBusquedaGrupo(e.target.value)}
              placeholder="Buscar grupo..."
              className="w-full h-7 px-2 rounded border border-border bg-transparent text-foreground focus:border-primary focus:outline-none text-[11px]"
              autoFocus
              data-testid={`grupo-buscar-${idx}`}
            />
            <div className="max-h-48 overflow-auto space-y-0.5">
              {(() => {
                const t = busquedaGrupo.trim().toLowerCase();
                const disponibles = gruposDisponibles.filter((g) => g.key !== grupoKey);
                const filtrados = t
                  ? disponibles.filter((g) => g.label.toLowerCase().includes(t) || g.key.toLowerCase().includes(t))
                  : disponibles;
                if (disponibles.length === 0) {
                  return <div className="text-muted-foreground italic px-2 py-1">No hay otros grupos</div>;
                }
                if (filtrados.length === 0) {
                  return <div className="text-muted-foreground italic px-2 py-1">Ningún grupo coincide con "{busquedaGrupo}"</div>;
                }
                return filtrados.map((g) => (
                  <button
                    key={g.key}
                    type="button"
                    onClick={() => onMoverA(g.key, g.label)}
                    className="w-full text-left px-2 py-1.5 rounded hover:bg-accent text-foreground flex items-center justify-between"
                    data-testid={`grupo-target-${idx}-${g.key.substring(0, 16)}`}
                  >
                    <span className="truncate">{g.label}</span>
                    <span className="text-muted-foreground text-[10px] flex-shrink-0 ml-2">×{g.count}</span>
                  </button>
                ));
              })()}
            </div>
            <div className="border-t border-border pt-2 space-y-1">
              <div className="text-[10px] uppercase font-bold text-muted-foreground">Crear grupo nuevo</div>
              <div className="flex gap-1">
                <input
                  value={nombreNuevo}
                  onChange={(e) => setNombreNuevo(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); onCrearNuevo(nombreNuevo); setNombreNuevo(""); } }}
                  placeholder="Nombre del nuevo grupo"
                  className="flex-1 h-7 px-2 rounded border border-border bg-transparent text-foreground focus:border-primary focus:outline-none"
                  data-testid={`grupo-nuevo-input-${idx}`}
                />
                <button
                  type="button"
                  onClick={() => { onCrearNuevo(nombreNuevo); setNombreNuevo(""); }}
                  className="h-7 px-2 rounded bg-primary text-primary-foreground text-[11px] font-medium hover:bg-primary/90"
                  data-testid={`grupo-nuevo-btn-${idx}`}
                >
                  Crear
                </button>
              </div>
            </div>
            {countEnGrupo > 1 && (
              <div className="border-t border-border pt-2">
                <button
                  type="button"
                  onClick={onSacar}
                  className="w-full text-left px-2 py-1.5 rounded hover:bg-amber-50 text-amber-800 text-[11px]"
                  data-testid={`grupo-sacar-btn-${idx}`}
                >
                  Sacar de este grupo (dejar solo)
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

